import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type JSX,
} from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { useGetMyAccountUsersExcludedDeactivated } from '@/api/queries/me/useGetMyAccountUsersExcludedDeactivated';
import useGetMyAccountTeams from '@/api/queries/me/useGetMyAccountTeams';
import { usePermissions } from '@/hooks/usePermissions';
import { useActiveAccountUsers } from '@/api/queries/me/useActiveAccountUsers';
import { EventDropArg } from '@fullcalendar/react';
import GridCalendar from '@payaca/components/calendar/GridCalendar';
import UserResourceCalendar, {
  pruneStaleTeamIds,
  resourceIdToUserId,
  TeamRef,
} from '@payaca/components/calendar/UserResourceCalendar';
import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { ScheduledEventsPermissions } from '@payaca/permissions/scheduledEvents/scheduled-events.permissions';
import { TasksPermissions } from '@payaca/permissions/tasks/tasks.permissions';
import * as scheduledEventActions from '@payaca/store/scheduledEvents/scheduledEventsActions';
import * as taskActions from '@payaca/store/tasks/tasksActions';
import { ScheduledEvent } from '@payaca/types/scheduledEventsTypes';

import { useCalendarSettings } from '../../../hooks/useCalendarSettings';
import { useAccount } from '@/api/rest/users/useAccount';
import { UNASSIGNED_NUMERIC_SENTINEL } from '../accountUserAvatarSelector/AccountUserAvatarSelector';
import { ScheduleContext } from '../contextProviders/ScheduleContextProvider';
import { useCollapsedScheduleGroups } from './useCollapsedScheduleGroups';

const Schedule: FC = (): JSX.Element => {
  const dispatch = useDispatch();
  const { userHasRequiredPermission } = usePermissions();
  const account = useAccount();
  const history = useHistory();
  const { url } = useRouteMatch();
  const applyScheduledEventPatch =
    useContext(ScheduleContext).applyScheduledEventPatch;

  const onDateSelect = (dateInfo?: {
    beginAt: string;
    endAt?: string;
    userAssignments?: number[];
  }) => {
    if (
      !userHasRequiredPermission([
        ScheduledEventsPermissions.ADD_EVENT,
        ScheduledEventsPermissions.ADD_MY_EVENT,
      ])
    ) {
      return;
    }
    // open modal with date selected or now
    history.push(`${url}/new`, {
      eventToCreate: {
        beginAt: dateInfo?.beginAt ? new Date(dateInfo?.beginAt) : undefined,
        endAt: dateInfo?.endAt ? new Date(dateInfo?.endAt) : undefined,
        userAssignments: dateInfo?.userAssignments,
      },
    });
  };

  // open modal to update event
  const onExistingEventSelect = (scheduledEvent: ScheduledEvent) => {
    history.push(`${url}/${scheduledEvent.id}`);
  };

  //update event by drag and drop
  const onEventDrop = useCallback(
    (info: EventDropArg) => {
      const existingUserAssignments: number[] =
        info.event.extendedProps.userAssignments ?? [];

      const oldUserAssignment = resourceIdToUserId(info.oldResource?.id);

      const newUserAssignment = resourceIdToUserId(info.newResource?.id);

      let updatedUserAssignments = existingUserAssignments;

      if (info.oldResource) {
        updatedUserAssignments = existingUserAssignments.filter(
          (x) => x !== oldUserAssignment
        );
        if (
          newUserAssignment !== undefined &&
          !updatedUserAssignments.includes(newUserAssignment)
        ) {
          updatedUserAssignments.push(newUserAssignment);
        }
      }

      const id = parseInt(info.event.id);

      const updates = {
        beginAt: info.event.start || undefined,
        endAt: info.event.end || undefined,
        userAssignments: updatedUserAssignments,
      };
      const previous = {
        beginAt: info.oldEvent.start || undefined,
        endAt: info.oldEvent.end || undefined,
        userAssignments: existingUserAssignments,
      };

      // Patch the local schedule state so re-renders don't revert the calendar
      // to the pre-drag data while the update request is in flight.
      applyScheduledEventPatch(id, updates);
      dispatch(
        scheduledEventActions.requestUpdateScheduledEvent(
          { id, ...updates },
          undefined,
          () => applyScheduledEventPatch(id, previous)
        )
      );
    },
    [applyScheduledEventPatch, dispatch]
  );

  // update event by drag to resize end time
  const onEventResize = (info: any) => {
    const id = parseInt(info.event.id);
    const previousEndAt = info.prevEvent?.end;

    applyScheduledEventPatch(id, { endAt: info.event.end });
    dispatch(
      scheduledEventActions.requestUpdateScheduledEvent(
        {
          id: id,
          endAt: info.event.end,
        },
        undefined,
        () => applyScheduledEventPatch(id, { endAt: previousEndAt })
      )
    );
  };

  const calendarRef = useContext(ScheduleContext).calendarRef;

  const {
    excludeNonBusinessHours,
    calendarType,
    groupByTeam,
    selectedTeamIds,
  } = useContext(ScheduleContext).calendarViewData;
  const scheduledEvents = useContext(ScheduleContext).scheduledEvents;
  const { assignedUserIds } = useContext(ScheduleContext).requestData;

  const activeUsers = useActiveAccountUsers();

  const { data: accountUsersWithTeams } =
    useGetMyAccountUsersExcludedDeactivated();
  const { data: accountTeams } = useGetMyAccountTeams({
    enabled: userHasRequiredPermission([AccountsPermissions.GET_TEAMS]),
  });

  const { userTeams, allTeams, teamMemberIds, activeTeamIds } = useMemo(() => {
    // Only scope by team once the teams have loaded. Until then (including
    // permanently, for a viewer without GET_TEAMS) treat the filter as
    // inactive: applying a persisted selection we can't validate — and with no
    // combobox to clear it — could blank the whole schedule.
    const activeTeamIds = accountTeams
      ? pruneStaleTeamIds(selectedTeamIds ?? [], accountTeams)
      : [];
    const userTeams = new Map<number, TeamRef[]>();
    const teamMemberIds = new Set<number>();
    (accountUsersWithTeams ?? []).forEach((user) => {
      const teams = user.teams.items;
      userTeams.set(Number(user.id), teams);
      if (
        activeTeamIds.length &&
        teams.some((team) => activeTeamIds.includes(team.id))
      ) {
        teamMemberIds.add(Number(user.id));
      }
    });
    const allTeams = [...(accountTeams ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    return { userTeams, allTeams, teamMemberIds, activeTeamIds };
  }, [accountUsersWithTeams, accountTeams, selectedTeamIds]);

  const { collapsedGroupIds, onGroupCollapsedChange } =
    useCollapsedScheduleGroups(allTeams);

  const viewType = calendarRef?.current?.getApi()?.view.type;

  const {
    businessStartTime,
    businessEndTime,
    businessDays,
    minTime,
    maxTime,
    hiddenDays,
    slotDuration,
    slotDurationClass,
  } = useCalendarSettings(excludeNonBusinessHours, viewType as string);

  // The set of users the schedule is scoped to (null = everyone). Explicit
  // avatar selection wins; otherwise a selected team scopes to its members
  // (even if that resolves to nobody, so an empty/loading team never falls back
  // to "everyone"). Selecting only the unassigned sentinel scopes to no users.
  const scopeUserIds = useMemo<Set<number> | null>(() => {
    if (assignedUserIds?.length) {
      return new Set(
        assignedUserIds.filter((id) => id !== UNASSIGNED_NUMERIC_SENTINEL)
      );
    }
    if (activeTeamIds.length) {
      return teamMemberIds;
    }
    return null;
  }, [assignedUserIds, activeTeamIds, teamMemberIds]);

  const showUnassigned = useMemo(
    () =>
      assignedUserIds?.length
        ? assignedUserIds.includes(UNASSIGNED_NUMERIC_SENTINEL)
        : userHasRequiredPermission([AccountsPermissions.GET_USERS]),
    [assignedUserIds, userHasRequiredPermission]
  );

  const filteredUsers = useMemo(
    () =>
      scopeUserIds
        ? activeUsers.filter((user) => scopeUserIds.has(user.id))
        : activeUsers,
    [activeUsers, scopeUserIds]
  );

  // Scope the events to the same set so Grid (which just renders whatever events
  // it's given) respects the team filter too. Unassigned events follow the row.
  const visibleEvents = useMemo(() => {
    const events = scheduledEvents ?? [];
    if (!scopeUserIds) return events;
    return events.filter((event) =>
      event.userAssignments?.length
        ? event.userAssignments.some((ua) => scopeUserIds.has(ua))
        : showUnassigned
    );
  }, [scheduledEvents, scopeUserIds, showUnassigned]);

  const canOnlyViewOwnTasks = useMemo(() => {
    return (
      !userHasRequiredPermission([TasksPermissions.PERSIST_TASK]) &&
      userHasRequiredPermission([TasksPermissions.PERSIST_SELF_CREATED_TASK])
    );
  }, [userHasRequiredPermission]);

  useEffect(() => {
    if (canOnlyViewOwnTasks) {
      dispatch(taskActions.clearTasks());
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [canOnlyViewOwnTasks]);

  const businessHours = {
    startTime: businessStartTime,
    endTime: businessEndTime,
    daysOfWeek: businessDays,
  };

  return (
    <>
      {calendarType === 'userResource' ? (
        <UserResourceCalendar
          scheduledEvents={visibleEvents}
          onDateSelect={onDateSelect}
          onExistingEventSelect={onExistingEventSelect}
          users={filteredUsers}
          showUnassigned={
            userHasRequiredPermission([
              ScheduledEventsPermissions.GET_EVENTS,
            ]) && showUnassigned
          }
          groupByTeam={groupByTeam && allTeams.length > 0}
          userTeams={userTeams}
          allTeams={allTeams}
          selectedTeamIds={selectedTeamIds}
          collapsedGroupIds={collapsedGroupIds}
          onGroupCollapsedChange={onGroupCollapsedChange}
          eventDrop={onEventDrop}
          eventResize={onEventResize}
          additionalCalendarProps={{
            slotDuration,
            slotMinWidth: 10,
          }}
          minTime={minTime}
          maxTime={maxTime}
          hiddenDays={hiddenDays}
          className={slotDurationClass}
          scheduledEventColourCoding={
            account.scheduledEventColourCoding ?? undefined
          }
          calendarRef={calendarRef}
          isExternallyControlled={true}
          businessHours={businessHours}
        />
      ) : (
        // !isUpdatingView && (
        <GridCalendar
          scheduledEvents={visibleEvents}
          onDateSelect={onDateSelect}
          onExistingEventSelect={onExistingEventSelect}
          eventDrop={onEventDrop}
          eventResize={onEventResize}
          editable={userHasRequiredPermission([
            ScheduledEventsPermissions.UPDATE_EVENT,
          ])}
          activeUsers={
            userHasRequiredPermission([AccountsPermissions.GET_USERS])
              ? activeUsers
              : null
          }
          minTime={minTime}
          maxTime={maxTime}
          hiddenDays={hiddenDays}
          additionalCalendarProps={{
            contentHeight: 'auto',
            scrollTimeReset: false,
          }}
          scheduledEventColourCoding={
            account.scheduledEventColourCoding ?? undefined
          }
          calendarRef={calendarRef}
          isExternallyControlled={true}
          businessHours={businessHours}
        />
      )}
    </>
  );
};

export default Schedule;
